/* WizardExecutionViewRenderer.js */

"use strict";
const _ = require("lodash");
const CreateInstanceDialog = require("../../customfeedback/createinstance/CreateInstanceDialogComponent");
const NIBLaunchDialog = require("../../customfeedback/niblaunch/NIBLaunchDialogComponent");
const Forms = require("@gluenetworks/forms");
const FormsComponent = Forms.ui.component;
const BaseRefResolver = Forms.resolvers.ref.base;
const Registrations = require("../../Registrations");
const ModalUserFeedback = require("@gluenetworks/modal_user_feedback").modalUserFeedback;

class WizardExecutionViewRenderer {
    constructor(model) {
        this._model = model;

        const _gameEditorRenderer = this._model._gameEditor.getRenderer();
        if (!_.isEmpty(_gameEditorRenderer)) {
            _gameEditorRenderer.getRootElement().classList.add("wizard-editor");
        }

        this._rootElem = document.createElement("wizard-execution-view-gl");
        this._rootElem.component = this._model;
        this._transitionUserFeedback = new ModalUserFeedback();

        this._pageDescriptionContainerElem = document.createElement("div");
        this._pageDescriptionContainerElem.classList.add("wizard-page-description-container");
        this._pageDescriptionElem = document.createElement("div");
        this._pageDescriptionElem.classList.add("wizard-page-description", "web-1-0");

        this._nibApiService = this._model._jnibAdapter.getNibApiService();
        this._userSession = this._model._userSession;
        this._refResolver = new BaseRefResolver(this._nibApiService, this._userSession);
        this._formContainer = document.createElement("div");
        this._formContainer.classList.add("form_container");
        this._formsComponent = new FormsComponent(this._refResolver);

        this._finishDisplayText = "Finish";
        this._registerListeners();
    }
    /********************************************/
    /* START PRIVATE COMPONENT RENDERER METHODS */
    /********************************************/
    _registerListeners() {
        this._formsComponent.on(
            "PendingPatchesChanged",
            (this._onPendingPatchesChanged = this._onPendingPatchesChanged.bind(this))
        );
        this._formsComponent.on(
            "DirtyStateChanged",
            (this._onDirtyStateChanged = this._onDirtyStateChanged.bind(this))
        );
        if (this._model.getRefLaunchState()) {
            this._formsComponent.on(
                "RefLaunched",
                (this._onRefLaunched = this._onRefLaunched.bind(this))
            );
        }
        this._formsComponent.on(
            "InvalidStatusChanged",
            (this._onInvalidStatusChanged = this._onInvalidStatusChanged.bind(this))
        );
        this._model.on(
            "RefLaunchStateChanged",
            (this._onRefLaunchStateChanged = this._onRefLaunchStateChanged.bind(this))
        );
        this._model.on(
            "ContinuationChanged",
            (this._onContinuationChanged = this._onContinuationChanged.bind(this))
        );
        this._model.on(
            "ContinuationFinished",
            (this._onContinuationFinished = this._onContinuationFinished.bind(this))
        );
        this._model.on(
            "StartNIBProgress",
            (this._onStartNIBProgress = this._onStartNIBProgress.bind(this))
        );
        this._model.on(
            "StopNIBProgress",
            (this._onStopNIBProgress = this._onStopNIBProgress.bind(this))
        );
        this._model.on(
            "UpdateNIBProgress",
            (this._onUpdateNIBProgress = this._onUpdateNIBProgress.bind(this))
        );
        this._model.on(
            "SetFinishButtonTitle",
            (this._onSetFinishButtonTitle = this._onSetFinishButtonTitle.bind(this))
        );
    }
    _unregisterListeners() {
        this._formsComponent.removeListener("PendingPatchesChanged", this._onPendingPatchesChanged);
        this._formsComponent.removeListener("DirtyStateChanged", this._onDirtyStateChanged);
        this._formsComponent.removeListener("RefLaunched", this._onRefLaunched);
        this._formsComponent.removeListener("InvalidStatusChanged", this._onInvalidStatusChanged);
        this._model.removeListener("RefLaunchStateChanged", this._onRefLaunchStateChanged);
        this._model.removeListener("ContinuationChanged", this._onContinuationChanged);
        this._model.removeListener("WizardFormRunChanged", this._onWizardFormRunChanged);
        this._model.removeListener("ContinuationFinished", this._onContinuationFinished);
        this._model.removeListener("StartNIBProgress", this._onStartNIBProgress);
        this._model.removeListener("StopNIBProgress", this._onStopNIBProgress);
        this._model.removeListener("UpdateNIBProgress", this._onUpdateNIBProgress);
        this._model.removeListener("SetFinishButtonTitle", this._onSetFinishButtonTitle);
    }
    /*******************************************/
    /* END PRIVATE COMPONENT RENDERER METHODS  */
    /* START PUBLIC COMPONENT RENDERER METHODS */
    /*******************************************/
    shutdown() {
        this._unregisterListeners();
        let promArr = [this._formsComponent.shutdown(), this._transitionUserFeedback.shutdown()];

        return Promise.all(promArr).then(() => {
            this._model = undefined;
            this._rootElem.remove();
            this._rootElem = undefined;
            this._formsComponent = undefined;
            this.nibProgressDialog = undefined;
            this._transitionUserFeedback = undefined;
        });
    }
    /**
     * Stateless method to completely rebuild this component from the _rootElem down.
     * @returns {Promise} An ES6 promise that lets the caller know when the component has finished rendering. If a non
     * Promise is returned the caller will assume the Renderer has completed rendering.
     */
    render() {
        //This will only render once... Not intended to be reentrant at this point
        if (!this._rootElem.hasChildNodes()) {
            this._rootElem.appendChild(this._transitionUserFeedback.renderer.rootElement);
            this._pageDescriptionContainerElem.appendChild(this._pageDescriptionElem);
            this._rootElem.appendChild(this._pageDescriptionContainerElem);
            this._rootElem.appendChild(this._formContainer);
            this._formContainer.appendChild(this._formsComponent.renderer.rootElement);

            this._transitionUserFeedback.getRenderer().getRootElement().classList.add("transition");
            return this._model.whenReady().then(() => {
                this._onWizardFormRunChanged();
                this._model.on(
                    "WizardFormRunChanged",
                    (this._onWizardFormRunChanged = this._onWizardFormRunChanged.bind(this))
                );
            });
        } else {
            return Promise.resolve();
        }
    }
    get rootElement() {
        return this.getRootElement();
    }
    getRootElement() {
        return this._rootElem;
    }
    get model() {
        return this.getModel();
    }
    getModel() {
        return this._model;
    }
    /*******************************************/
    /* END COMPONENT RENDERER METHODS */
    /* START PRIVATE RENDER METHODS */
    /*******************************************/
    _rerender() {
        return this._clearTransition()
            .then(() => {
                return this._disableActionButtons();
            })
            .then(() => {
                return this._renderWizardDetails();
            })
            .then(() => {
                return this._renderPageDetails();
            })
            .then(() => {
                return this._renderTheForm();
            })
            .then(() => {
                return this._renderViewActions();
            });
    }

    /**
     * Disables next, back, finish action buttons (if are on current page) before re-rendering the wizard page
     * @private
     */
    _disableActionButtons() {
        this._model.gameEditor.disableViewAction("next");
        this._model.gameEditor.disableViewAction("back");
        this._model.gameEditor.disableViewAction("finish");
    }

    _renderWizardDetails() {}
    _renderPageDetails() {
        let wizFormRun = this._model.getWizardFormRun();
        let pageTitle = wizFormRun.getCurPageTitle();
        //TODO need to expose this functionality
        let viewName = this._model.gameEditor.getViewName();
        if (pageTitle) {
            this._model._gameEditorTitleCmpnt.setTitle(viewName + " - " + pageTitle);
        } else {
            this._model._gameEditorTitleCmpnt.setTitle(viewName);
        }
        let pageDescription = wizFormRun.getCurPageDescription();
        if (pageDescription) {
            this._pageDescriptionElem.innerHTML = pageDescription;
            this._pageDescriptionContainerElem.classList.remove("hidden");
        } else {
            this._pageDescriptionContainerElem.classList.add("hidden");
            this._pageDescriptionElem.innerHTML = "";
        }
    }
    _renderViewActions() {
        let wizFormRun = this._model.getWizardFormRun();
        let gameEditor = this._model.gameEditor;
        if (this._model.cancelled) {
            return Promise.resolve();
        }
        return Promise.resolve()
            .then(() => {
                return gameEditor.clearViewActions();
            })
            .then(() => {
                //Download
                if (wizFormRun.showBackBtn()) {
                    gameEditor.addViewAction({
                        type: "normal",
                        displayName: "< Back",
                        name: "back",
                        handler: (this._onBackButtonClick = this._onBackButtonClick.bind(this)),
                    });
                }
                //Next
                if (wizFormRun.showNextBtn()) {
                    gameEditor.addViewAction({
                        type: "normal",
                        displayName: "Next >",
                        name: "next",
                        handler: () => {
                            this._onNextButtonClick();
                        },
                    });
                }
                //Finish
                if (wizFormRun.showFinishBtn()) {
                    gameEditor.addViewAction({
                        type: "normal",
                        displayName: this._finishDisplayText,
                        name: "finish",
                        handler: (this._onFinishButtonClick = this._onFinishButtonClick.bind(this)),
                    });
                }
                //Cancel
                if (wizFormRun.showCancelBtn()) {
                    gameEditor.addViewAction({
                        type: "normal",
                        displayName: "Cancel",
                        name: "cancel",
                        handler: (this._onCancelButtonClick = this._onCancelButtonClick.bind(this)),
                    });
                }
                if (this._formsComponent.isFormInvalid()) {
                    this._onInvalidStatusChanged({newValue: true});
                }
            });
    }
    _renderTheForm() {
        // Wipe out any data that was in the _formsComponent
        this._formsComponent.clearPendingPatches();
        this._formsComponent._clearPristineValue();

        let wizFormRun = this._model.getWizardFormRun();

        //let typeInfo = wizFormRun.getCurPageExpandedJType();
        let pristineValue = wizFormRun.getCurPageData();

        let jtype = wizFormRun.getCurPageJType();

        if (!_.isEmpty(jtype)) {
            this._formsComponent.renderer.rootElement.classList.remove("hidden");
            this._pageDescriptionContainerElem.classList.remove("full-height");
            this._formsComponent.setTypeInformation(jtype, true);
            this._formsComponent._setPristineValue(pristineValue, true);
            return this._formsComponent.renderer.render();
        } else {
            this._formsComponent.renderer.rootElement.classList.add("hidden");
            this._pageDescriptionContainerElem.classList.add("full-height");
            this._formsComponent.setTypeInformation(jtype, true);
            this._formsComponent._setPristineValue(pristineValue, true);
            return this._formsComponent.renderer.render();
        }
    }

    _clearTransition() {
        this._startTransition = false;
        if (this._transitionTimer) {
            clearTimeout(this._transitionTimer);
            this._transitionTimer = undefined;
        }
        // This is a hack because the tranition veil has already started and
        // There is no way to stop the tween.
        if (this.tweenStartTime) {
            let tweenRunTime = Date.now() - this.tweenStartTime;
            if (tweenRunTime < 1000) {
                // Dismiss in the future
                let self = this;
                return new Promise((res /*, reject*/) => {
                    setTimeout(() => {
                        Promise.resolve()
                            .then(() => {
                                self.nibProgressDialog = undefined;
                                return self._transitionUserFeedback.hideDisplayed();
                            })
                            .then((result) => {
                                res(result);
                            });
                    }, 1000);
                });
            }
        }
        return Promise.resolve().then(() => {
            this.nibProgressDialog = undefined;
            return this._transitionUserFeedback.hideDisplayed();
        });
    }

    /**
     * Render the transition page in the wizard editor.  While rendering,
     * the process will check after each promise to verify that a new wizard page has not been
     * invoked.  If a new wizard page was started at any time, then the rendering of the transition
     * page will be stopped.
     * @returns {Promise<void>}
     * @private
     */
    _renderTransition() {
        return Promise.resolve()
            .then(() => {
                if (this._startTransition) {
                    return this._model.gameEditor.clearViewActions();
                } else {
                    throw new Error();
                }
            })
            .then(() => {
                if (this._startTransition) {
                    return this._model.gameEditor.addViewAction({
                        type: "normal",
                        displayName: "Cancel",
                        name: "cancel",
                        handler: (this._onCancelButtonClick = this._onCancelButtonClick.bind(this)),
                    });
                } else {
                    throw new Error();
                }
            })
            .then(() => {
                if (this._startTransition) {
                    this.tweenStartTime = Date.now();
                    return this._transitionUserFeedback.displayCustom(this.nibProgressDialog, {
                        notDismissable: true,
                    });
                } else {
                    throw new Error();
                }
            })
            .catch((/*e*/) => {
                console.info("Transition page stopped by new wizard page.");
            });
    }
    /*******************************************/
    /* END PRIVATE RENDER METHODS */
    /* START HANDLER METHODS */
    /*******************************************/
    _onRefLaunched(deets) {
        if (_.has(this, "_model._userFeedback")) {
            let dialog = new CreateInstanceDialog(
                this._refResolver,
                this._nibApiService,
                this._userSession,
                {
                    title: "Create An Instance",
                    refLaunchedDetails: deets,
                    selectedType: null,
                    instanceData: null,
                }
            );
            return this._model._userFeedback
                .displayCustom(dialog, {
                    notDismissable: true,
                })
                .then((/*value*/) => {
                    //TODO anyway to autoselect a ref???
                    return this._renderTheForm();
                })
                .catch((e) => {
                    this._model._logger.warn({error: e}, "Create Instance Dialog failed.");
                    return Promise.resolve();
                });
        }
    }
    _onInvalidStatusChanged(e) {
        let wizFormRun = this._model.getWizardFormRun();
        if (e.newValue === true) {
            if (wizFormRun.showNextBtn()) {
                this._model._gameEditor.disableViewAction("next");
            }
            if (wizFormRun.showFinishBtn()) {
                this._model._gameEditor.disableViewAction("finish");
            }
        } else {
            if (wizFormRun.showNextBtn()) {
                this._model._gameEditor.enableViewAction("next");
            }
            if (wizFormRun.showFinishBtn()) {
                this._model._gameEditor.enableViewAction("finish");
            }
        }
    }
    _onPendingPatchesChanged() {
        // console.log("Patches Changed",this._formsComponent.getPendingPatches());
        // console.log("New Value is ",this._formsComponent.getValue());
    }
    _onDirtyStateChanged() {
        // console.log("Dirty State Changed",this._formsComponent.isDirty());
    }
    _onContinuationChanged() {
        let className = this._model._jnibAdapter.getCssClassFromContinuation(
            this._model.continuation
        );
        this._rootElem.classList.add(className);
    }

    /**
     * Handle a change in the whether to listen the ref launch events.
     * @param state
     * @private
     */
    _onRefLaunchStateChanged() {
        if (this._model.getRefLaunchState()) {
            this._formsComponent.on(
                "RefLaunched",
                (this._onRefLaunched = this._onRefLaunched.bind(this))
            );
        } else {
            this._formsComponent.removeListener("RefLaunched", this._onRefLaunched);
        }
    }

    _onWizardFormRunChanged() {
        this._rerender();
    }
    _onContinuationFinished(/*finishObj*/) {
        this._model.gameEditor.clearViewActions().then(() => {
            //Restart Action
            this._model.gameEditor.addViewAction({
                type: "normal",
                displayName: "Restart",
                name: "restart",
                handler: () => {
                    this._model.gameEditor.runAction(
                        Registrations.WIZARD_INSTANCE_EDITOR_NAVIGATE_TO_EXECUTION_VIEW_ACTION
                    );
                },
            });
            this._model.gameEditor.addViewAction({
                type: "normal",
                displayName: "Close",
                name: "close",
                handler: () => {
                    this._model.gameEditor.closeEditor(
                        this._model.gameEditor.editorState.editorId,
                        true
                    );
                },
            });

            //TODO change View Actions
            this._model.gameEditor.showDefaultMessageView({
                message: "Workflow has completed. Click here to run it again.",
                icon: "wizard.wizardEditor",
                action: Registrations.WIZARD_INSTANCE_EDITOR_NAVIGATE_TO_EXECUTION_VIEW_ACTION,
            });
        });
    }
    _onBackButtonClick() {
        let value = this._formsComponent.getValue();
        this._formsComponent.clearPendingPatches();
        this._formsComponent._clearPristineValue();
        this._model.wizardFormRun.buttonPressed("back", value);
    }
    _onNextButtonClick() {
        let value = this._formsComponent.getValue();
        this._model.wizardFormRun.buttonPressed("next", value);
    }
    _onFinishButtonClick() {
        let value = this._formsComponent.getValue();
        this._formsComponent.clearPendingPatches();
        this._formsComponent._clearPristineValue();
        this._model.wizardFormRun.buttonPressed("finish", value);
    }
    _onCancelButtonClick() {
        let value = this._formsComponent.getValue();
        this._formsComponent.clearPendingPatches();
        this._formsComponent._clearPristineValue();

        // There is a case where the wizard has not started yet, but the cancel button is displayed
        // for the transition pane.
        if (this._model.wizardFormRun) {
            this._model.wizardFormRun.buttonPressed("cancel", value);
        }
        this.nibProgressDialog = undefined;
        this._transitionUserFeedback.hideDisplayed();
        this._model.setCancelled(true);
    }
    _onStartNIBProgress() {
        // Make the code re-entrant
        if (!this._startTransition) {
            // Create the progress dialog before we render it because we can get notifications
            // on it before it's actually displayed.
            this.nibProgressDialog = new NIBLaunchDialog(this._userSession, {
                title: "Workflow Progress",
            });
            this._startTransition = true;
            this._transitionTimer = setTimeout(() => {
                this._transitionTimer = undefined;
                this._renderTransition();
            }, 300);
        }
    }

    _onStopNIBProgress(progressObj) {
        // If the run has finished, then the buttons should not be added.
        if (_.get(progressObj, "runFinished", false)) {
            return this._clearTransition();
        } else {
            return this._renderViewActions().then(() => {
                return this._clearTransition();
            });
        }
    }

    _onUpdateNIBProgress(progressObj) {
        if (this.nibProgressDialog) {
            this.nibProgressDialog.updateNIBProgress(progressObj);
        }
    }

    _onSetFinishButtonTitle(title) {
        this._finishDisplayText = title;
    }
    /*******************************************/
    /* END HANDLER METHODS */
    /* START PRIVATE API METHODS */
    /*******************************************/
    /*******************************************/
    /*  END PRIVATE API METHODS */
    /*******************************************/
}
module.exports = WizardExecutionViewRenderer;
